'use client';
import React from 'react';

import styled, { TC } from '@benzinga/themetron';
import { StockSymbol } from '@benzinga/session';
import { PerformanceColor } from './PerformanceColor';
import { TickerPopover } from './TickerPopover';
import { numberPercent } from '../../utils';
import { ChevronRight } from '@benzinga/themed-icons';
import { useTrackSymbol } from '@benzinga/hooks';

interface Props {
  dialogMode?: boolean;
  onSymbolClick: (symbol: StockSymbol) => void;
  percentChange?: number | null;
  price?: string | null;
  symbol: StockSymbol;
}

const shouldUpdate = (prevProps: React.PropsWithChildren<Props>, nextProps: React.PropsWithChildren<Props>) => {
  return !(
    prevProps.percentChange !== nextProps.percentChange ||
    prevProps.price !== nextProps.price ||
    prevProps.symbol !== nextProps.symbol ||
    prevProps.dialogMode !== nextProps.dialogMode ||
    prevProps.children !== nextProps.children
  );
};

const emptyCell = <span />;

const TickerSnippet: React.FC<React.PropsWithChildren<Props>> = React.memo(props => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isContentHovered, setIsContentHovered] = React.useState(false);
  const { onMouseEnter, onMouseLeave } = useTrackSymbol(props.symbol);

  const handleMouseEnter = React.useCallback(() => {
    onMouseEnter();
    setIsHovered(true);
  }, [onMouseEnter]);

  const handleMouseLeave = React.useCallback(() => {
    onMouseLeave();
    setIsHovered(false);
  }, [onMouseLeave]);

  const handleContentMouseEnter = React.useCallback(() => {
    setIsContentHovered(true);
  }, []);

  const handleContentMouseLeave = React.useCallback(() => {
    setIsContentHovered(false);
  }, []);

  const onSymbolClick = React.useCallback(() => {
    const onSymbolClick = props.onSymbolClick;
    onSymbolClick?.(props.symbol);
  }, [props.onSymbolClick, props.symbol]);

  const { children, percentChange, price, symbol } = props;

  // Render individually to maintain table layout
  let priceDisplay = emptyCell;
  let performanceDisplay = emptyCell;
  let arrowDisplay = emptyCell;
  let tooltipDisplay = emptyCell;

  const label = numberPercent(percentChange ? percentChange : 0, false) || '';

  if (price) {
    priceDisplay = <ItemSpan>{price}</ItemSpan>;
    performanceDisplay = (
      <ItemSpan>
        <PerformanceColor label={label} stat={percentChange} />
      </ItemSpan>
    );
    arrowDisplay = (
      <ItemSpan>
        <ChevronRight title="More Details" />
      </ItemSpan>
    );
    tooltipDisplay = (
      <TooltipItemSpan onMouseEnter={handleContentMouseEnter} onMouseLeave={handleContentMouseLeave}>
        {children}
      </TooltipItemSpan>
    );
  }

  return (
    <TickerPopover
      content={tooltipDisplay}
      positions={['right', 'left']}
      visible={props.dialogMode || isHovered || isContentHovered}
    >
      <ContainerDiv className="tickerSnippet" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <TC.StretchRow>
          <TC.Row>
            <SymbolItemSpan onClick={onSymbolClick}>{symbol}</SymbolItemSpan>
            {priceDisplay}
            {performanceDisplay}
          </TC.Row>
          {arrowDisplay}
        </TC.StretchRow>
      </ContainerDiv>
    </TickerPopover>
  );
}, shouldUpdate);

const ContainerDiv = styled.div`
  padding-left: 10px;
  width: 100%;
`;

const ItemSpan = styled.span`
  padding: 3px;
`;

const SymbolItemSpan = styled.span`
  padding: 3px 3px 3px 10px;
`;

const TooltipItemSpan = styled.span`
  position: relative;
`;

export default TickerSnippet;
